// routes/_app.$usernameRaw.artworks.($subTab).tsx
import debug from 'debug'
import CSREntry from 'src/clientRoutes/_app.$usernameRaw.artworks.($subTab).csr'
import FullSSREntry from 'src/clientRoutes/_app.$usernameRaw.artworks.($subTab).full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import type { UserGetUserArtworksLatestResponse } from 'src/services/orpc/generated'
import { userProfileHandle, userProfileLoader } from 'src/utils/router/route'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import type { SeoServerConfig } from 'src/utils/ssr-meta/consul.server'
import {
  DEFAULT_TITLE,
  profileMeta,
  profileMetaLoader,
} from 'src/utils/ssr-meta/seo'
import type { Route } from './+types/_app.$usernameRaw.artworks.($subTab)'

const log = debug('pixai:ssr-meta')

export const handle = {
  ...userProfileHandle,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

/**
 * Mirrors the variables `<BaseCursorPaging>` synthesises for the default
 * artworks tab on first mount:
 *
 *   - `authorId: user.id`
 *   - `last: 20` (default `pageSize`, no `before`/`after`,
 *     `isOrderOptionPosSeq(undefined) === false` ⇒ pagination from the
 *     end)
 *   - `isNsfw: false` (`useShowNsfw` defaults to `false` ⇒ NSFW hidden ⇒
 *     `isNsfw: !showNsfw ? false : undefined` evaluates to `false`)
 *   - `isTheme`, `types`, `visibilityType`, `orderBy`, `before`, `after`
 *     all left `undefined`, which Apollo drops from the cache key.
 *
 * `useListArtworks` additionally injects `tackLanguage: locale` before
 * calling `useQuery`, so we mirror that here too. Drift on any of these
 * variables ⇒ cache miss ⇒ the SSR pre-fetch is wasted and the live
 * grid will refetch on hydrate (visible flash). Keep this in lockstep
 * with `<PersonalArtworkListForAll>` /
 * `<AllArtworksList>` (`src/components/PersonalArtworkList/index.tsx`).
 */
const SSR_LIST_ARTWORKS_PAGE_SIZE = 20

/**
 * Mirror the input that `<BaseCursorPaging>` -> `useListUserArtworks` will
 * send on first mount of the default `/@<user>/artworks` tab.
 *
 * Source of truth (live grid):
 *   `<AllArtworksList>` (`PersonalArtworkList/index.tsx`) passes
 *     `extraArgs: { userId, sort: 'latest', visibility: undefined,
 *                   type: 'artwork' }`
 *   merged with `<BaseCursorPaging isPosSeq pageSize=20>` cursor params
 *   (= `{ first: 20 }` when no cursor in the URL hash).
 *
 * `useListUserArtworks` then passes that object straight through as
 * `input` to `orpc.user.getUserArtworksLatest.queryOptions({ input })`,
 * which is also what we use for the SSR seed below — so the
 * react-query queryKey we write into will be the exact same one the
 * live `useQuery` reads from on first render.
 */
function buildSsrUserArtworksInput(userId: string) {
  return {
    userId,
    first: SSR_LIST_ARTWORKS_PAGE_SIZE,
    after: undefined,
    last: undefined,
    before: undefined,
    visibility: undefined,
    type: 'artwork' as const,
  }
}

/**
 * Server-side fetch of the user's "latest" artworks (the default sort
 * tab) via the same oRPC endpoint that `useListUserArtworks` will hit
 * client-side. Used to seed react-query's cache so the first SSR
 * paint emits the real 20-card grid into `<main>` instead of a
 * spinner.
 *
 * Returns `null` on any error (caller's `.catch(() => null)` upstream
 * still kicks in defensively); the loader skips the react-query seed
 * and the live grid renders its skeleton until the post-hydrate fetch
 * lands. There used to be a `spawnSync('python3', ...)` `urllib.request`
 * fallback here mirroring the (now also removed) home-route fallback,
 * but shelling out to a Python interpreter on every SSR failure was a
 * brittle no-extra-dep workaround that masked real upstream issues
 * (missing CA bundle in the container, DNS races, etc.); the SDK path
 * has been reliable enough in dev / staging that letting the loader
 * degrade to `null` + the skeleton fallback is a cleaner failure mode.
 */
async function fetchUserArtworksLatestForSSR(
  userId: string,
): Promise<UserGetUserArtworksLatestResponse | null> {
  const [{ userGetUserArtworksLatest }, { createClient }] = await Promise.all([
    import('src/services/orpc/generated/sdk.gen'),
    import('src/services/orpc/generated/client'),
  ])

  const client = createClient({
    baseUrl: process.env.PIXAI_API_URL || 'https://api.pixai.art',
    credentials: 'omit',
  })

  try {
    const result = await userGetUserArtworksLatest({
      client,
      path: { userId },
      query: { first: SSR_LIST_ARTWORKS_PAGE_SIZE, type: 'artwork' },
      throwOnError: true,
    })
    return result.data ?? null
  } catch {
    return null
  }
}

/**
 * SSR loader for user artworks page.
 *
 * The user profile chrome (`ApolloProvider`, `UserProfileProvider`,
 * `UserProfileInfo`, etc.) is now mounted by the parent
 * `_app.$usernameRaw.tsx` *layout* route, which also pre-fetches and
 * seeds the `User` document into the route-scoped Apollo cache. So
 * this child loader only needs to compute SEO meta for the artworks
 * tab and pre-fetch the first page of `ListArtworks` so the live
 * `<BaseCursorPaging>` hits cache on first mount and the SSR HTML
 * already ships the 20 first-screen artwork cards.
 */
export async function loader({ params, request, context }: Route.LoaderArgs) {
  const { bucketString, parseUsername } =
    await import('src/utils/ssr-meta/url.server')
  const { fetchUserForSSR } =
    await import('src/utils/ssr-meta/graphql/user.server')
  const { getSeoConfig } = await import('src/utils/ssr-meta/consul.server')

  /**
   * Determine if a user profile should be noindexed based on SEO rollout rules.
   */
  function shouldNoIndexUser(
    username: string,
    likesReceived: number,
    config: Required<SeoServerConfig>,
  ): boolean {
    const { gradualRolloutPercent, userIndexedMinimalLikes } = config
    const inRolloutBucket = bucketString(username, 100) < gradualRolloutPercent

    return inRolloutBucket && likesReceived < userIndexedMinimalLikes
  }

  const usernameRaw = params.usernameRaw
  if (!usernameRaw) {
    log('artist artworks loader missing usernameRaw url=%s', request.url)
    return profileMetaLoader({ requestUrl: request.url })
  }

  const username = parseUsername(usernameRaw)
  if (!username) {
    log(
      'artist artworks loader invalid usernameRaw=%s url=%s',
      usernameRaw,
      request.url,
    )
    return profileMetaLoader({ requestUrl: request.url })
  }

  const { t } = getI18n(context)

  const [user, seoConfig] = await Promise.all([
    fetchUserForSSR(username),
    getSeoConfig(),
  ])

  if (!user) {
    log(
      'artist artworks loader user not found username=%s url=%s',
      username,
      request.url,
    )
    return profileMetaLoader({ requestUrl: request.url })
  }

  const likesReceived = user.artworkLikedCount ?? 0
  const shouldNoIndex = shouldNoIndexUser(username, likesReceived, seoConfig)
  if (shouldNoIndex) {
    log(
      'artist artworks loader noindex username=%s likes=%s url=%s',
      username,
      likesReceived,
      request.url,
    )
  }

  const i18nTitle = user.displayName
    ? t('common:meta.title.user.home', { username: user.displayName })
    : DEFAULT_TITLE

  let avatarUrl = user.avatarSmallThumbnailMediaUrl || undefined
  if (avatarUrl) {
    avatarUrl = avatarUrl.replace('/images/avatar/', '/images/stillThumb/')
  }
  const meta = profileMetaLoader({
    requestUrl: request.url,
    title: i18nTitle || DEFAULT_TITLE,
    image: avatarUrl,
    twitterCard: avatarUrl ? 'summary' : 'summary_large_image',
  })

  // Pre-fetch the first page of artworks via the same oRPC endpoint
  // the live `<BaseCursorPaging>` -> `useListUserArtworks` will hit
  // on first mount. The input shape comes from
  // `buildSsrUserArtworksInput` so the react-query queryKey our seed
  // writes into matches what the live hook reads from. Falls back to
  // `null` (no seed → live grid renders its spinner) on fetch
  // failure so we don't block the rest of the page response on a
  // transient artwork API issue.
  const userArtworksInput = buildSsrUserArtworksInput(user.id)
  const userArtworksRawResponse = await fetchUserArtworksLatestForSSR(
    user.id,
  ).catch(error => {
    log(
      'artist artworks loader list artworks fetch failed userId=%s err=%o',
      user.id,
      error,
    )
    return null
  })

  return {
    ...meta,
    user,
    noIndex: shouldNoIndex,
    artworksReactQuerySeed: userArtworksRawResponse
      ? {
          input: userArtworksInput,
          // Raw oRPC response shape kept verbatim so the FullSSREntry
          // can write it straight into react-query's cache for
          // `useListUserArtworks` (default `latest` sort).
          rawResponse: userArtworksRawResponse,
        }
      : null,
  }
}

/** Meta function for SSR SEO. Same as /@username. */
export function meta({ loaderData }: Route.MetaArgs) {
  return profileMeta(loaderData)
}

export async function clientLoader(args: Route.ClientLoaderArgs) {
  await userProfileLoader({
    ...args,
    params: { ...args.params, tab: 'artworks' },
  })
  return args.serverLoader?.()
}
clientLoader.hydrate = true

/**
 * Dual-mode entry. Control (CSR) path renders `UserProfileContent`
 * directly under the parent layout's route-scoped Apollo client with
 * no SSR seed — `<BaseCursorPaging>` issues its own `ListArtworks`
 * query and shows skeletons until it lands. Treatment (Full SSR)
 * path mounts the writeQuery seed bridge defined in
 * `clientRoutes/_app.$usernameRaw.artworks.($subTab).full-ssr.tsx`.
 *
 * The parent `_app.$usernameRaw.tsx` layout still mounts its own
 * route-scoped Apollo provider in either branch, so
 * `<UserProfileProvider>` consumers see a stable client across SSR
 * / hydrate boundaries regardless of which entry is active.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
